import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

export type MotionLevel = 'full' | 'reduced' | 'off';
export type MotionPreference = MotionLevel | 'auto';

interface MotionContextValue {
  preference: MotionPreference;
  setPreference: (preference: MotionPreference) => void;
  level: MotionLevel;
  setLevel: (level: MotionLevel) => void;
  isFull: boolean;
  isReduced: boolean;
  isOff: boolean;
}

const MotionContext = createContext<MotionContextValue | undefined>(undefined);

const STORAGE_KEY = 'nexus_motion_preference_v1';

const readStoredPreference = (): MotionPreference => {
  if (typeof window === 'undefined') return 'auto';
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === 'full' || raw === 'reduced' || raw === 'off' || raw === 'auto') return raw;
  return 'auto';
};

const computeAutoLevel = (): MotionLevel => {
  if (typeof window === 'undefined') return 'reduced';

  const prefersReducedMotion =
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  if (prefersReducedMotion) return 'reduced';

  const connection = (navigator as unknown as { connection?: { saveData?: boolean } }).connection;
  if (connection?.saveData) return 'reduced';

  const deviceMemory = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
  const cores = navigator.hardwareConcurrency;

  // Relaxed thresholds: only degrade on very low-end devices
  if (typeof deviceMemory === 'number' && deviceMemory <= 2) return 'reduced';
  if (typeof cores === 'number' && cores <= 2) return 'reduced';

  return 'full';
};

const probeFps = (sampleFrames = 30): Promise<number> => {
  if (typeof window === 'undefined') return Promise.resolve(0);

  return new Promise((resolve) => {
    let frames = 0;
    let start = 0;
    let last = 0;

    const tick = (t: number) => {
      if (!start) start = t;
      last = t;
      frames += 1;

      if (frames >= sampleFrames) {
        const seconds = (last - start) / 1000;
        const fps = seconds > 0 ? frames / seconds : 0;
        resolve(fps);
        return;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
};

export const MotionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<MotionPreference>(() => readStoredPreference());
  const [level, setLevel] = useState<MotionLevel>(() => {
    if (preference === 'auto') return computeAutoLevel();
    return preference;
  });

  const setPreference = (next: MotionPreference) => {
    setPreferenceState(next);
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, next);
  };

  useEffect(() => {
    if (preference !== 'auto') {
      setLevel(preference);
      return;
    }

    const applyAuto = () => setLevel(computeAutoLevel());
    applyAuto();

    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const onChange = () => applyAuto();
    if (media) {
      if (typeof media.addEventListener === 'function') media.addEventListener('change', onChange);
      else media.addListener(onChange);
    }

    return () => {
      if (!media) return;
      if (typeof media.removeEventListener === 'function') media.removeEventListener('change', onChange);
      else media.removeListener(onChange);
    };
  }, [preference]);

  useEffect(() => {
    if (preference !== 'auto') return;
    // Only run FPS probe if currently at 'full' level
    if (level !== 'full') return;

    let canceled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const runProbe = () => {
      void probeFps(60).then((fps) => {
        if (canceled) return;
        // Skip if page is not visible
        if (typeof document !== 'undefined' && document.visibilityState && document.visibilityState !== 'visible') {
          return;
        }
        // More lenient thresholds
        if (fps > 0 && fps < 20) setLevel('off');
        else if (fps > 0 && fps < 35) setLevel('reduced');
      });
    };

    const scheduleProbe = () => {
      if (timeoutId !== null) clearTimeout(timeoutId);
      // Delay probe to allow page to stabilize after initial load
      timeoutId = setTimeout(runProbe, 1200);
    };

    // If page is not visible, wait for visibility change
    if (typeof document !== 'undefined' && document.visibilityState && document.visibilityState !== 'visible') {
      const onVisibilityChange = () => {
        if (document.visibilityState !== 'visible') return;
        document.removeEventListener('visibilitychange', onVisibilityChange);
        scheduleProbe();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      return () => {
        canceled = true;
        if (timeoutId !== null) clearTimeout(timeoutId);
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    }

    scheduleProbe();

    return () => {
      canceled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [preference, level]);

  const value = useMemo<MotionContextValue>(
    () => ({
      preference,
      setPreference,
      level,
      setLevel,
      isFull: level === 'full',
      isReduced: level === 'reduced',
      isOff: level === 'off',
    }),
    [level, preference]
  );

  return <MotionContext.Provider value={value}>{children}</MotionContext.Provider>;
};

export const useMotion = () => {
  const ctx = useContext(MotionContext);
  if (!ctx) throw new Error('useMotion must be used within a MotionProvider');
  return ctx;
};

